import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useStrava } from '../contexts/StravaContext';
import TempoVisualization from './TempoVisualization';
import ArtworkGenerator from './ArtworkGenerator';
import SocialShareModal from './SocialShareModal';

const { FiArrowLeft, FiShare2, FiDownload, FiBarChart2, FiImage } = FiIcons;

const ActivityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activities } = useStrava();
  const [activeTab, setActiveTab] = useState('visualization');
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [currentArtwork, setCurrentArtwork] = useState(null);
  const [currentImage, setCurrentImage] = useState(null);

  const activity = activities.find(a => a.id.toString() === id);

  if (!activity) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center text-white">
          <h2 className="text-2xl font-bold mb-4">Activity not found</h2>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-orange-400 hover:text-orange-300 transition-colors"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const formatPace = (metersPerSecond) => {
    const minutesPerKm = 1000 / (metersPerSecond * 60);
    const minutes = Math.floor(minutesPerKm);
    const seconds = Math.floor((minutesPerKm - minutes) * 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}/km`;
  };

  const handleShareClick = (artwork, image) => {
    setCurrentArtwork(artwork);
    setCurrentImage(image);
    setShareModalOpen(true);
  };

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          className="flex items-center justify-between mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-white hover:text-orange-400 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
            Back to Dashboard
          </button>
          <div className="flex items-center gap-3">
            <motion.button
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShareModalOpen(true)}
            >
              <SafeIcon icon={FiShare2} className="w-4 h-4" />
              Share
            </motion.button>
            <motion.button
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white rounded-xl transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <SafeIcon icon={FiDownload} className="w-4 h-4" />
              Export
            </motion.button>
          </div>
        </motion.div>

        {/* Activity Title */}
        <motion.div
          className="mb-6"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <h1 className="text-2xl font-bold text-white">{activity.name}</h1>
          <p className="text-gray-400">
            {new Date(activity.start_date).toLocaleDateString()} • {(activity.distance / 1000).toFixed(2)}km • {formatPace(activity.average_speed)} avg pace
          </p>
        </motion.div>

        {/* Tabs */}
        <motion.div
          className="flex gap-4 mb-6 border-b border-white/10 pb-2"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <button
            onClick={() => setActiveTab('visualization')}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-all ${
              activeTab === 'visualization' ? 'text-orange-400 border-b-2 border-orange-400' : 'text-gray-400 hover:text-white'
            }`}
          >
            <SafeIcon icon={FiBarChart2} className="w-4 h-4" />
            Tempo Analysis
          </button>
          <button
            onClick={() => setActiveTab('artwork')}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-all ${
              activeTab === 'artwork' ? 'text-orange-400 border-b-2 border-orange-400' : 'text-gray-400 hover:text-white'
            }`}
          >
            <SafeIcon icon={FiImage} className="w-4 h-4" />
            AI Artwork
          </button>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {activeTab === 'visualization' ? (
            <motion.div
              key="visualization"
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              <TempoVisualization activity={activity} formatPace={formatPace} />
            </motion.div>
          ) : (
            <motion.div
              key="artwork"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              <ArtworkGenerator 
                activity={activity} 
                onShare={handleShareClick} 
              />
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* Share Modal */}
      <SocialShareModal 
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        artwork={currentArtwork || {
          title: activity.name,
          poem: `A ${activity.type.toLowerCase()} of ${(activity.distance / 1000).toFixed(2)}km at ${formatPace(activity.average_speed)} pace.`
        }}
        image={currentImage || {
          url: "https://images.unsplash.com/photo-1571008887538-b36bb32f4571?w=600&h=600&fit=crop"
        }}
      />
    </div>
  );
};

export default ActivityDetail;